import { PercentIcon } from "lucide-react";
import { forwardRef, useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";

import { Button, ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useForwardedRef } from "@/hooks/use-forwarded-ref";
import { cn } from "@/lib/utils";

interface ColorPickerProps {
  value: {
    color: string;
    opacity: number;
  };
  onChange: (value: string) => void;
  onOpacityChange: (value: number) => void;
  onBlur?: () => void;
}

const ColorPicker = forwardRef<
  HTMLInputElement,
  Omit<ButtonProps, "value" | "onChange" | "onBlur"> & ColorPickerProps
>(
  (
    {
      disabled,
      value = { color: "#000", opacity: 100 },
      onChange,
      onOpacityChange,
      onBlur,
      name,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const [open, setOpen] = useState(false);

    const parsedValue = useMemo(() => {
      return value.color || "#FFFFFF";
    }, [value]);

    return (
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild disabled={disabled} onBlur={onBlur}>
          <div className='flex justify-between items-center w-[180px]'>
            <div className='flex items-center gap-2 '>
              <Button
                {...props}
                className={cn("block w-8 h-8", className)}
                name={name}
                onClick={() => {
                  setOpen(true);
                }}
                size='icon'
                style={{
                  backgroundColor: parsedValue,
                }}
                variant='outline'
              />
              <p>{value.color}</p>
              <div />
            </div>
            <div>{value.opacity}%</div>
          </div>
        </PopoverTrigger>
        <PopoverContent className='w-full p-1'>
          <HexColorPicker color={parsedValue} onChange={onChange} />

          <div className='grid grid-cols-[0.6fr_0.4fr] items-center border rounded-sm  max-w-[200px] '>
            <Input
              maxLength={7}
              onChange={(e) => {
                onChange(e?.currentTarget?.value);
              }}
              ref={ref}
              value={parsedValue}
              className='border-0 focus-visible:ring-0'
            />
            <Input
              maxLength={3}
              max={100}
              value={value.opacity || 0}
              icon={{
                icon: PercentIcon,
                position: "right",
              }}
              pattern='\d*'
              onChange={(e): { color: string; opacity: number } | void => {
                const inputValue = Number(e.target.value);
                // Check if the input value is greater than 100 or NaN
                if (inputValue > 100 || isNaN(inputValue)) {
                  // Return the current value to prevent further processing
                  return value;
                }
                // Call the onOpacityChange function with the validated input value
                onOpacityChange(inputValue);
                // Explicitly return undefined to match the expected return type

                return undefined;
              }}
              className='border-0 border-l-2 rounded-l-none focus-visible:ring-0'
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
ColorPicker.displayName = "ColorPicker";

export { ColorPicker };
